import { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { useUser } from './UserContext';
import { useOrganization } from './OrganizationContext';
import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
   fetchUserSubscriptionsByEmail,
   clearSubscription,
   setSubscription,
   Subscription,
} from '../store/slices/subscriptionSlice';
import { hasSubscriptionAccess, shouldRedirectToSubscription } from '../utils/subscriptionUtils';
import { checkInvitedUser } from '../services/invitedUserService';
import { Dialog, DialogTitle, Typography, DialogContent, Button, IconButton } from '@mui/material';
import { Box } from '@mui/material';
import { Modal } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';

interface SubscriptionContextType {
   currentSubscription: Subscription | null;
   setCurrentSubscription: (subscription: Subscription | null) => void;
   subscriptionModalOpen: boolean;
   setSubscriptionModalOpen: (open: boolean) => void;
   hasSubscriptions: boolean;
   allSubscriptions: Subscription[];
   fetchUserSubscriptions: (forceRefresh?: boolean) => Promise<any>; // Only for forced refreshes (e.g., after payment)
   loading: boolean;
   error: string | null;
   checkSubscriptionStatus: () => boolean;
   isInvitedUser: boolean;
   refreshInvitationStatus: () => Promise<void>;
   invitationLoading: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
   currentSubscription: null,
   setCurrentSubscription: (subscription: Subscription | null) => {},
   subscriptionModalOpen: false,
   setSubscriptionModalOpen: (open: boolean) => {},
   hasSubscriptions: false,
   allSubscriptions: [],
   fetchUserSubscriptions: async () => {},
   loading: false,
   error: null,
   checkSubscriptionStatus: () => false,
   isInvitedUser: false,
   refreshInvitationStatus: async () => {},
   invitationLoading: true,
});

export const useSubscription = (): any => {
   const context = useContext(SubscriptionContext);
   const { currentUser } = useUser();

   // Get fresh Redux state directly
   const {
      currentSubscription: reduxSubscription,
      hasActiveSubscription: reduxHasSubscriptions,
      loading: reduxLoading,
   } = useAppSelector((state) => state.subscription);

   if (!context) {
      throw new Error('useSubscription must be used within a SubscriptionProvider');
   }

   // Override checkSubscriptionStatus to use fresh Redux state and invitation status
   const checkSubscriptionStatus = () => {
      // ⚠️  IMPORTANT: This function checks both subscription and invitation status (synchronously)

      console.log('🚀 checkSubscriptionStatus: CALLED - Starting subscription check');

      if (reduxLoading || context.invitationLoading) {
         console.log('useSubscription: Still loading data, skipping check', {
            reduxLoading,
            invitationLoading: context.invitationLoading,
         });
         return false;
      }

      const hasSubscription = reduxHasSubscriptions;
      const hasInvitedFlag = context.isInvitedUser;
      const hasCurrentSubscription = reduxSubscription !== null;

      // 🛡️ SUBSCRIPTION PROTECTION GUARD
      // Skip protection for public routes (shared collections, albums, etc.)
      const currentPath = window.location.pathname;
      const isPublicRoute =
         currentPath.startsWith('/shared/') ||
         currentPath.startsWith('/social/') ||
         currentPath.startsWith('/public') ||
         currentPath.startsWith('/qr/') ||
         currentPath.startsWith('/profile/public/') ||
         currentPath === '/login' ||
         currentPath === '/signup' ||
         currentPath === '/' ||
         currentPath === '/features' ||
         currentPath === '/about' ||
         currentPath === '/ambassador';

      if (isPublicRoute) {
         console.log(
            '✅ PROTECTION GUARD: Public route detected, skipping subscription check:',
            currentPath
         );
         return true;
      }

      // If user has subscription or is invited, always allow access
      if (hasSubscription || hasInvitedFlag) {
         const userType = hasInvitedFlag ? 'invited user' : 'user with subscription';

         return true;
      }

      // Redirect non-invited users with no subscription history to public subscription page
      if (!hasInvitedFlag && !hasCurrentSubscription) {
         console.log(
            '🚨 PROTECTION GUARD: Non-invited user with no subscription history - redirecting to public subscription'
         );
         if (currentUser?.uid && currentUser?.email) {
            window.location.href = `/public-subscription?userId=${currentUser.uid}&email=${encodeURIComponent(currentUser.email)}&reason=no_subscription`;
            return false;
         } else {
            console.error('🚨 PROTECTION GUARD: Cannot redirect - missing user information');
            return false;
         }
      }

      // For users with subscription history (even if canceled/expired), show modal instead of redirect
      if (!hasSubscription) {
         const userType = hasInvitedFlag ? 'invited user' : 'non-invited user';
         const subscriptionType = hasCurrentSubscription
            ? 'with subscription history'
            : 'without subscription history';
         console.log(
            `❌ useSubscription: ${userType} ${subscriptionType} without active access, showing modal`
         );
         context.setSubscriptionModalOpen(true);
         return false;
      }

      const userType = hasInvitedFlag ? 'invited user' : 'non-invited user';
      console.log(`✅ useSubscription: ${userType} with subscription access, access granted`);
      return true;
   };

   // Create refreshSubscriptions function
   const refreshSubscriptions = async () => {
      if (currentUser?.email) {
         return context.fetchUserSubscriptions(true); // Force refresh
      }
   };

   // Return context with overridden checkSubscriptionStatus and fresh Redux data
   return {
      ...context,
      checkSubscriptionStatus,
      currentSubscription: reduxSubscription,
      hasSubscriptions: reduxHasSubscriptions,
      loading: reduxLoading,
      isInvitedUser: context.isInvitedUser,
      refreshInvitationStatus: context.refreshInvitationStatus,
      // Ensure subscriptions array is always available (even if empty)
      subscriptions: reduxSubscription ? [reduxSubscription] : [],
      allSubscriptions: reduxSubscription ? [reduxSubscription] : [],
      refreshSubscriptions,
   };
};

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
   const { currentUser } = useUser();
   const dispatch = useAppDispatch();
   const [hasInitialFetch, setHasInitialFetch] = useState(false);
   const [isFetching, setIsFetching] = useState(false);
   const [isInvitedUser, setIsInvitedUser] = useState(false);
   const [invitationLoading, setInvitationLoading] = useState(true); // Track invitation loading

   // Get subscription data from Redux store
   const {
      currentSubscription: reduxSubscription,
      allSubscriptions: reduxAllSubscriptions,
      hasActiveSubscription: reduxHasSubscriptions,
      loading: reduxLoading,
      error: reduxError,
   } = useAppSelector((state) => state.subscription);

   const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

   // Use Redux data as the source of truth
   const subscription = reduxSubscription;
   const hasSubscriptions = reduxHasSubscriptions;
   const allSubscriptions = reduxAllSubscriptions;
   const loading = reduxLoading;
   const error = reduxError;

   // Function to manually set subscription (if needed)
   const setCurrentSubscription = (newSubscription: Subscription | null) => {
      if (newSubscription) {
         dispatch(setSubscription(newSubscription));
      } else {
         dispatch(clearSubscription());
      }
   };

   const fetchUserSubscriptions = async (forceRefresh = false) => {
      if (!currentUser?.email) {
         throw new Error('No user email available');
      }

      // Always return cached data unless explicitly forcing refresh
      if (!forceRefresh && subscription !== null) {
         console.log('SubscriptionProvider: Using cached subscription data (no API call)');
         return { subscription, hasActiveSubscription: hasSubscriptions };
      }

      // Prevent multiple concurrent fetches
      if (isFetching && !forceRefresh) {
         console.log('SubscriptionProvider: Already fetching, skipping duplicate request');
         return { subscription, hasActiveSubscription: hasSubscriptions };
      }

      // Only fetch if forcing refresh or no data exists
      try {
         setIsFetching(true);
         console.log('SubscriptionProvider: Fetching subscription data', {
            forceRefresh,
            reason: forceRefresh ? 'forced refresh' : 'no cached data',
         });
         const resultAction = await dispatch(
            fetchUserSubscriptionsByEmail(currentUser.email) as any
         );
         if (fetchUserSubscriptionsByEmail.fulfilled.match(resultAction)) {
            return resultAction.payload;
         } else if (fetchUserSubscriptionsByEmail.rejected.match(resultAction)) {
            throw new Error(resultAction.payload as string);
         }
      } catch (error: any) {
         throw new Error(error || 'Failed to fetch subscriptions');
      } finally {
         setIsFetching(false);
      }
   };

   // Check invitation status when user changes
   useEffect(() => {
      const checkInvitationStatus = async () => {
         setInvitationLoading(true); // Start loading

         if (currentUser?.email && currentUser?.uid) {
            try {
               // ONLY check for invited flag in user document (strict check)
               let hasInvitedFlag = false;
               try {
                  const userDocRef = doc(db, 'users', currentUser.uid);
                  const userDoc = await getDoc(userDocRef);
                  if (userDoc.exists()) {
                     hasInvitedFlag = userDoc.data()?.invited === true;
                  }
               } catch (error) {
                  console.error('Error checking invited flag:', error);
               }

               // Set isInvitedUser ONLY if user has invited: true in their document
               setIsInvitedUser(hasInvitedFlag);

               console.log('🔍 SubscriptionProvider: Invitation status checked:', {
                  email: currentUser.email,
                  uid: currentUser.uid,
                  hasInvitedFlag,
                  note: 'STRICT CHECK: Only users.invited === true get bypass',
                  timestamp: new Date().toISOString(),
               });

               // If no invitation flag found, log for debugging
               if (!hasInvitedFlag) {
                  console.log(
                     'ℹ️ SubscriptionProvider: No invited flag (invited !== true) - user must have subscription for access'
                  );
               }
            } catch (error) {
               console.error('Error checking invitation status:', error);
               setIsInvitedUser(false);
            }
         } else {
            setIsInvitedUser(false);
         }

         setInvitationLoading(false); // End loading
      };

      checkInvitationStatus();
   }, [currentUser?.email, currentUser?.uid]); // Also trigger when user ID changes (login/logout)

   // Function to manually refresh invitation status (useful after signup/login)
   const refreshInvitationStatus = async () => {
      if (currentUser?.email && currentUser?.uid) {
         try {
            // ONLY check for invited flag in user document (strict check)
            let hasInvitedFlag = false;
            try {
               const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
               if (userDoc.exists()) {
                  hasInvitedFlag = userDoc.data()?.invited === true;
               }
            } catch (error) {
               console.error('Error checking invited flag during refresh:', error);
            }

            // Set isInvitedUser ONLY if user has invited: true in their document
            setIsInvitedUser(hasInvitedFlag);

            console.log('🔄 SubscriptionProvider: Manually refreshed invitation status:', {
               email: currentUser.email,
               uid: currentUser.uid,
               hasInvitedFlag,
               note: 'STRICT CHECK: Only users.invited === true get bypass',
               timestamp: new Date().toISOString(),
            });
         } catch (error) {
            console.error('Error refreshing invitation status:', error);
            setIsInvitedUser(false);
         }
      }
   };

   // Auto-fetch subscription ONLY when user logs in, clear when user logs out
   useEffect(() => {
      console.log('🔍 SubscriptionProvider: useEffect triggered', {
         'currentUser?.uid': currentUser?.uid,
         'currentUser?.email': currentUser?.email,
         hasInitialFetch,
         loading,
         isFetching,
         reduxLoading,
      });

      if (!currentUser?.uid) {
         console.log('SubscriptionProvider: User logged out, clearing subscription data');
         dispatch(clearSubscription());
         setHasInitialFetch(false);
         setIsFetching(false);
         setIsInvitedUser(false);
         return;
      }

      // Force fetch subscription data when user is logged in
      if (currentUser.email && !isFetching) {
         console.log(
            '🚀 SubscriptionProvider: User detected - fetching subscription data:',
            currentUser.email
         );
         console.log('🔍 SubscriptionProvider: Current state before fetch:', {
            hasInitialFetch,
            loading,
            isFetching,
            reduxLoading,
            currentSubscription: subscription,
            reduxHasSubscriptions,
         });

         setIsFetching(true);
         dispatch(fetchUserSubscriptionsByEmail(currentUser.email) as any).finally(() => {
            setIsFetching(false);
            setHasInitialFetch(true);
         });
      } else {
         console.log('🔍 SubscriptionProvider: Skipping fetch, conditions not met:', {
            hasEmail: !!currentUser.email,
            isFetching,
            currentUser: currentUser?.email,
         });
      }
   }, [currentUser?.uid, currentUser?.email, dispatch]);

   // Define checkSubscriptionStatus here where it has access to Redux data
   // NOTE: Components using useSubscription() will get the overridden version with fresh Redux state
   const checkSubscriptionStatus = () => {
      // ⚠️  IMPORTANT: This function checks both subscription and invitation status (synchronously)

      if (loading) {
         console.log('SubscriptionProvider: Still loading subscription data, skipping check');
         return false;
      }

      const hasAccess = reduxHasSubscriptions;
      const hasCurrentSubscription = reduxSubscription !== null;

      // 🛡️ SUBSCRIPTION PROTECTION GUARD
      // Skip protection for public routes (shared collections, albums, etc.)
      const currentPath = window.location.pathname;
      const isPublicRoute =
         currentPath.startsWith('/shared/') ||
         currentPath.startsWith('/social/') ||
         currentPath.startsWith('/public') ||
         currentPath.startsWith('/qr/') ||
         currentPath.startsWith('/profile/public/') ||
         currentPath === '/login' ||
         currentPath === '/signup' ||
         currentPath === '/' ||
         currentPath === '/features' ||
         currentPath === '/about' ||
         currentPath === '/ambassador';

      if (isPublicRoute) {
         console.log(
            '✅ PROTECTION GUARD: Public route detected, skipping subscription check:',
            currentPath
         );
         return true;
      }

      // If user has subscription or is invited, always allow access
      if (hasAccess || isInvitedUser) {
         const userType = isInvitedUser ? 'invited user' : 'user with subscription';

         return true;
      }

      // Redirect non-invited users with no subscription history to public subscription page
      if (!isInvitedUser && !hasCurrentSubscription) {
         console.log(
            '🚨 PROTECTION GUARD: Non-invited user with no subscription history - redirecting to public subscription'
         );
         if (currentUser?.uid && currentUser?.email) {
            window.location.href = `/public-subscription?userId=${currentUser.uid}&email=${encodeURIComponent(currentUser.email)}&reason=no_subscription`;
            return false;
         } else {
            console.error('🚨 PROTECTION GUARD: Cannot redirect - missing user information');
            return false;
         }
      }

      // For users with subscription history (even if canceled/expired), show modal instead of redirect
      if (!hasAccess) {
         const userType = isInvitedUser ? 'invited user' : 'non-invited user';
         const subscriptionType = hasCurrentSubscription
            ? 'with subscription history'
            : 'without subscription history';
         console.log(
            `❌ SubscriptionProvider: ${userType} ${subscriptionType} without active access, showing modal`
         );
         setSubscriptionModalOpen(true);
         return false;
      }

      const userType = isInvitedUser ? 'invited user' : 'non-invited user';
      console.log(`✅ SubscriptionProvider: ${userType} with subscription access, access granted`);
      return true;
   };

   return (
      <SubscriptionContext.Provider
         value={{
            currentSubscription: subscription,
            setCurrentSubscription,
            subscriptionModalOpen,
            setSubscriptionModalOpen,
            hasSubscriptions,
            allSubscriptions,
            fetchUserSubscriptions,
            loading,
            error,
            checkSubscriptionStatus,
            isInvitedUser,
            refreshInvitationStatus,
            invitationLoading,
         }}
      >
         {children}
         {subscriptionModalOpen && (
            <SubscriptionModal
               isOpen={subscriptionModalOpen}
               onClose={() => setSubscriptionModalOpen(false)}
            />
         )}
      </SubscriptionContext.Provider>
   );
};

const SubscriptionModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
   const { currentUser } = useUser();

   const handleUpgradeClick = () => {
      if (currentUser?.uid && currentUser?.email) {
         window.location.href = `/public-subscription?userId=${currentUser.uid}&email=${encodeURIComponent(currentUser.email)}`;
      } else {
         console.error('User information not available for subscription upgrade');
      }
   };

   return (
      <Dialog
         open={isOpen}
         onClose={onClose}
         maxWidth='sm'
         fullWidth
         PaperProps={{
            sx: {
               borderRadius: 2,
               p: 1,
            },
         }}
      >
         <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <Typography variant='h6' sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  Subscription Required
               </Typography>
               <IconButton
                  onClick={onClose}
                  size='small'
                  sx={{
                     color: 'text.secondary',
                     '&:hover': { backgroundColor: 'action.hover' },
                  }}
               >
                  <CloseIcon />
               </IconButton>
            </Box>
         </DialogTitle>
         <DialogContent>
            <Typography variant='body1' sx={{ mb: 2, color: 'text.secondary' }}>
               You're currently on the free plan. This feature is only available with a paid
               subscription.
            </Typography>
            <Typography variant='body2' sx={{ mb: 3, color: 'text.secondary' }}>
               Upgrade now to unlock all premium features and get unlimited access.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
               <Button variant='outlined' onClick={onClose} sx={{ minWidth: 100 }}>
                  Cancel
               </Button>
               <Button
                  color='primary'
                  variant='contained'
                  onClick={handleUpgradeClick}
                  disabled={!currentUser?.uid || !currentUser?.email}
                  sx={{ minWidth: 100 }}
               >
                  Upgrade Now
               </Button>
            </Box>
         </DialogContent>
      </Dialog>
   );
};
